import type { ReactNode } from 'react';
import { CORAL_DEEP, INK, LINE, MUTED, PAPER, PROGRESS_FILL, GRAD, SHADOW_GLOW, MOTION } from '../ui/theme.ts';
import { Icon } from '../ui/Icon.tsx';
import { asset } from '../engine/assets.ts';
import { haptic } from '../engine/haptics.ts';
import type { SegmentProgress } from '../engine/navigation.ts';

export interface CtaState {
  label?: string | null;
  caption?: string | null;
  icon?: string | null;
  badge?: string | null;
  disabled?: boolean;
  spinner?: boolean;
  large?: boolean;
  onPress?: () => void;
}

export function Header({ canGoBack, onBack, brand }: { canGoBack: boolean; onBack: () => void; brand: string }) {
  return (
    <header style={{ height: 56, flex: 'none', display: 'grid', gridTemplateColumns: '44px 1fr 44px', alignItems: 'center', padding: '0 12px' }}>
      <button
        type="button"
        onClick={onBack}
        aria-label="Back"
        style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
          color: INK,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          visibility: canGoBack ? 'visible' : 'hidden',
        }}
      >
        <Icon name="caret-left" size={22} />
      </button>
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontWeight: 800, fontSize: 19, letterSpacing: '-.03em' }}>
        <img src={asset('boostsy-icon')} alt="" width={28} height={28} style={{ width: 28, height: 28 }} />
        {brand.toLowerCase()}
      </span>
      <span />
    </header>
  );
}

export function Progress({ label, segments }: { label: string; segments: SegmentProgress[] }) {
  return (
    <div style={{ flex: 'none', padding: '0 20px 4px', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: CORAL_DEEP }}>{label}</div>
      <div style={{ display: 'flex', gap: 6, width: '100%' }} role="progressbar" aria-label="Progress" aria-valuemin={0} aria-valuemax={100}
        aria-valuenow={Math.round(segments.reduce((a, s) => a + s.width, 0) / segments.length)}>
        {segments.map((s, i) => (
          <div key={i} style={{ flex: 1, height: 8, borderRadius: 999, background: LINE, overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 999, background: PROGRESS_FILL, width: `${s.width}%`, transition: 'width .3s' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function Footer({ cta }: { cta: CtaState }) {
  if (!cta.label) return null;
  const disabled = !!cta.disabled;
  return (
    <footer
      className="fk-footer"
      style={{
        flex: 'none',
        padding: '16px 16px calc(16px + env(safe-area-inset-bottom))',
        background: 'rgba(255,255,255,.85)',
        backdropFilter: 'blur(12px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        boxShadow: `0 -1px 0 ${LINE}`,
      }}
    >
      <button
        type="button"
        className="fk-cta"
        onClick={() => {
          if (disabled) return;
          haptic();
          cta.onPress?.();
        }}
        aria-disabled={disabled}
        disabled={disabled}
        style={{
          minHeight: cta.large ? 56 : 54,
          width: '100%',
          borderRadius: 999,
          border: 0,
          background: GRAD,
          color: '#fff',
          fontWeight: 800,
          fontSize: 16,
          cursor: disabled ? 'default' : 'pointer',
          boxShadow: SHADOW_GLOW,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          opacity: disabled ? 0.45 : 1,
          transition: `transform ${MOTION.fast}ms`,
        }}
      >
        {cta.icon ? <Icon name={cta.icon} size={20} style={cta.spinner ? { animation: 'fk-spin 1s linear infinite' } : undefined} /> : null}
        {cta.label}
        {cta.badge ? (
          <span style={{ padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,.25)', fontSize: 13 }}>{cta.badge}</span>
        ) : null}
      </button>
      {cta.caption ? (
        <div style={{ textAlign: 'center', fontSize: 13, color: MUTED, fontWeight: 600 }} aria-live="polite">
          {cta.caption}
        </div>
      ) : null}
    </footer>
  );
}

/** The 390-design column: full width to 480, centred on the cream backdrop above 767px. */
export function Column({ children, stepId, stepType }: { children: ReactNode; stepId: string; stepType: string }) {
  return (
    <div className="fk-viewport" style={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch' }}>
      <div
        data-funnel-step={stepId}
        data-step-type={stepType}
        className="fk-column"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 480,
          background: PAPER,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 18px 44px rgba(31,27,45,.08)',
        }}
      >
        {children}
      </div>
    </div>
  );
}
